import { membershipNameType } from './constant'

/**
 * Back-to-School 2026 — 50% off the first 3 months of Standard or Pro, monthly billing only,
 * for an explicitly invited list of Free learners.
 *
 * Unlike the date-gated promos (Dragon Boat, CNY, UNESCO), the audience here is NOT derivable on
 * the client: it is a seeded allow-list in Mongo, surfaced by the profile endpoint as
 * `backToSchool2026.isEligible`. Everything in this module is therefore presentation only —
 * the server re-checks the allow-list, the plan, the price and the window before honouring the
 * coupon (see dev-backend/src/common/back-to-school-2026.ts). Nothing here can grant a discount.
 *
 * Scope notes:
 *  - Monthly only. "First 3 months" is meaningless on an annual plan, and the yearly tab stays
 *    at list price — which is why an eligible learner is landed on the monthly tab by default.
 *  - Standard AND Pro, unlike Dragon Boat (Pro only).
 *  - The 3-day Pro trial STACKS with the discount rather than being replaced by it: the trial
 *    is collected as a Stripe trial_period_days on the same subscription the coupon discounts,
 *    so an eligible first-timer gets "3 days free, then 50% off for 3 months". With a 3-day
 *    trial the monthly invoices land inside the coupon's 3-month window, so all three
 *    post-trial invoices are still discounted.
 */
export const BACK_TO_SCHOOL_2026 = {
  couponCode: 'JD3BTS2026',
  // Anchored to Hong Kong time (UTC+8), matching the rest of the app's story cadence.
  // Keep these in sync with dev-backend/src/common/back-to-school-2026.ts.
  startDate: new Date('2026-08-28T00:00:00+08:00'),
  endDate: new Date('2026-09-01T23:59:59+08:00'),
  discountPercent: 50,
  durationMonths: 3,
  billing: 'monthly' as const,
} as const

export type PromoTimeLeft = {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const emptyTimeLeft = (): PromoTimeLeft => ({ days: 0, hours: 0, minutes: 0, seconds: 0 })

export function isBackToSchool2026Active(now: Date = new Date()): boolean {
  return now >= BACK_TO_SCHOOL_2026.startDate && now <= BACK_TO_SCHOOL_2026.endDate
}

export function getBackToSchool2026EndDate(): Date {
  return BACK_TO_SCHOOL_2026.endDate
}

export function getBackToSchool2026TimeLeft(now: Date = new Date()): PromoTimeLeft {
  const diff = BACK_TO_SCHOOL_2026.endDate.getTime() - now.getTime()
  if (diff <= 0) return emptyTimeLeft()
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  }
}

export type BackToSchoolAudienceContext = {
  /** `backToSchool2026.isEligible` from the profile endpoint — the seeded allow-list. */
  isAllowListed?: boolean
  membershipName?: string
  now?: Date
}

/**
 * Active window AND invited AND still on Free: the gate for showing the campaign anywhere.
 *
 * The membership re-check is not redundant with the server's. The profile response is cached in
 * localStorage and rehydrated on load, so a learner who upgraded in another tab can briefly carry
 * a stale `isEligible: true`. Re-deriving from the live membership stops us offering an upgrade
 * discount to someone who already upgraded.
 */
export function isUserEligibleForBackToSchool2026({
  isAllowListed,
  membershipName,
  now = new Date(),
}: BackToSchoolAudienceContext): boolean {
  if (!isAllowListed) return false
  if (membershipName !== membershipNameType.Free) return false
  return isBackToSchool2026Active(now)
}

/**
 * Whether a given plan card on /setting#plan should wear the campaign treatment: the discounted
 * price, the "50% off for 3 months" framing, and the campaign CTA.
 *
 * A manually entered voucher always wins — the learner chose it, and silently overriding it with
 * our coupon would show a price we then wouldn't charge.
 */
export function shouldApplyBackToSchool2026ToPlan({
  planValue,
  billing,
  isCurrentPlan,
  hasManualCoupon,
  isAllowListed,
  membershipName,
  now = new Date(),
}: {
  planValue?: string
  billing: 'monthly' | 'yearly'
  isCurrentPlan: boolean
  hasManualCoupon: boolean
  isAllowListed?: boolean
  membershipName?: string
  now?: Date
}): boolean {
  if (hasManualCoupon) return false
  if (isCurrentPlan) return false
  if (billing !== BACK_TO_SCHOOL_2026.billing) return false
  if (planValue !== membershipNameType.Standard && planValue !== membershipNameType.Pro) return false
  return isUserEligibleForBackToSchool2026({ isAllowListed, membershipName, now })
}

/**
 * Derive displayed pricing from the live Stripe monthly price. Returns null when the price is
 * unavailable so callers can render a fallback rather than invent a number — prices are never
 * hardcoded here (regional pricing means the "list price" differs per visitor).
 */
export function getBackToSchool2026Pricing(
  regularMonthlyPrice?: number | null,
): { regularMonthly: number; promoMonthly: number; savedOverPromo: number } | null {
  if (!regularMonthlyPrice || isNaN(regularMonthlyPrice) || regularMonthlyPrice <= 0) return null
  const promoMonthly = (regularMonthlyPrice * (100 - BACK_TO_SCHOOL_2026.discountPercent)) / 100
  return {
    regularMonthly: regularMonthlyPrice,
    promoMonthly,
    savedOverPromo: (regularMonthlyPrice - promoMonthly) * BACK_TO_SCHOOL_2026.durationMonths,
  }
}
